import React, { useLayoutEffect, type CSSProperties, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import Sidebar from './Sidebar';
import HeaderBar from './HeaderBar';
import MobileTabBar from './MobileTabBar';
import { NewBuildBanner } from '../NewBuildBanner';

const SIDEBAR_WIDTH_VAR = '--app-sidebar-width';

/** The expanded sidebar's width; the e2e privacy-banner check measures it. */
const SIDEBAR_WIDTH = '232px';
/** The icon rail's width: shadcn's `--sidebar-width-icon`. */
const SIDEBAR_WIDTH_ICON = '3rem';

interface AppLayoutProps {
  /** Search box for the top bar (#2477). */
  headerSearch?: ReactNode;
}

/** Everything right of the sidebar; reads the sidebar's state from its provider. */
function Shell({ headerSearch }: AppLayoutProps) {
  const { state, isMobile, toggleSidebar, setOpenMobile } = useSidebar();

  // Publish the sidebar's current width for fixed overlays mounted outside
  // this layout, such as `PrivacyBanner`: 232px expanded, the icon rail when
  // collapsed (#2511), nothing on a phone (the sidebar is a sheet there). Only
  // this layout renders the sidebar, so public routes see the variable unset.
  const width = isMobile ? '0px' : state === 'expanded' ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_ICON;
  useLayoutEffect(() => {
    document.body.style.setProperty(SIDEBAR_WIDTH_VAR, width);
  }, [width]);
  useLayoutEffect(() => {
    const style = document.body.style;
    return () => {
      style.removeProperty(SIDEBAR_WIDTH_VAR);
    };
  }, []);

  return (
    <main className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      {/* Above the header and in-flow, so a stale tab is told so on every
          authenticated page and the notice pushes the shell down instead
          of covering a control (#2246). Renders nothing until the served
          build id stops matching this bundle's. */}
      <NewBuildBanner />

      <HeaderBar onMenuClick={toggleSidebar} search={headerSearch} />

      {/* `relative` makes this scroller the containing block for
          absolutely-positioned descendants (e.g. Tailwind sr-only
          spans). Without it they escape to the document and stretch
          html.scrollHeight — the recurring double-scrollbar bug.

          Page padding moved wholly into `PageContainer` (#1392 D1) so
          there is a single source; this scroller keeps only
          layout-critical classes. */}
      <div className="relative flex-1 overflow-auto min-w-0">
        <Outlet />
      </div>

      {/* The phone bottom bar (#2487): in the column after the scroller,
          never fixed, so it covers no content. More opens the drawer. */}
      {isMobile ? <MobileTabBar onMore={() => setOpenMobile(true)} /> : null}
    </main>
  );
}

/**
 * The authenticated shell. The sidebar (shadcn's Sidebar, #2511) is pinned
 * from `md` up and collapses to an icon rail; below `md` it is a sheet the
 * phone tab bar opens. The provider owns both states.
 */
const AppLayout: React.FC<AppLayoutProps> = ({ headerSearch }) => (
  // `dvh` tracks the iOS toolbar; `h-screen` stays the fallback without it.
  <SidebarProvider
    style={{ '--sidebar-width': SIDEBAR_WIDTH } as CSSProperties}
    className="h-screen supports-[height:100dvh]:h-dvh min-h-0 overflow-hidden bg-background font-sans antialiased"
  >
    <Sidebar />
    <Shell headerSearch={headerSearch} />
  </SidebarProvider>
);

export default AppLayout;
